import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { EmailTagInput } from '../../components/ui/email-tag-input';
import { 
  Mail, 
  Clock, 
  Send, 
  Loader2, 
  CheckCircle,
  BarChart3,
  FileText,
  DollarSign,
  AlertTriangle,
  Calendar,
  RefreshCw
} from 'lucide-react';

export const GerenciaReport = () => {
  const [config, setConfig] = useState({
    enabled: false,
    schedule_hour: 8,
    schedule_minute: 0,
    notification_emails: [],
    last_sent: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/gerencia/report-config');
      setConfig({
        ...res.data,
        notification_emails: res.data.notification_emails || []
      });
    } catch (err) {
      // No-op: GerenciaReport puede ser cargado por usuarios sin permiso de
      // configuración. Logueamos para no perder el rastro en debugging.
      console.error('[GerenciaReport] loadConfig failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async () => {
    if (config.notification_emails.length === 0) {
      alert('Agregue al menos un email de notificación');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/gerencia/report-config', {
        enabled: config.enabled,
        schedule_hour: config.schedule_hour,
        schedule_minute: config.schedule_minute,
        notification_emails: config.notification_emails
      });
      await loadConfig();
      alert('Configuración guardada correctamente');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    if (config.notification_emails.length === 0) {
      alert('Agregue al menos un email de notificación primero');
      return;
    }
    
    setSending(true);
    try {
      // Save config first
      await api.post('/gerencia/report-config', {
        enabled: config.enabled,
        schedule_hour: config.schedule_hour,
        schedule_minute: config.schedule_minute,
        notification_emails: config.notification_emails
      });
      
      const res = await api.post('/gerencia/send-report-now');
      alert(res.data.message);
      await loadConfig();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error enviando informe');
    } finally {
      setSending(false);
    }
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await api.get('/gerencia/report-preview');
      setPreviewData(res.data);
    } catch (_err) {
      alert('Error cargando vista previa');
    } finally {
      setLoadingPreview(false);
    }
  };

  const fmt = (n) => {
    if (!n && n !== 0) return '$0';
    return `$${Math.round(n).toLocaleString('es-CL')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Informe Gerencial Diario</h1>
        <p className="text-sm text-muted-foreground">
          Configurar envío automático de informe con KPIs para gerencia
        </p>
      </div>

      {/* Configuration Card */}
      <div className="bg-white rounded-lg border p-4">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" />
          Configuración de Envío
        </h2>
        
        <div className="space-y-4">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-gray-600" />
              <div>
                <p className="font-medium">Informe Diario Automático</p>
                <p className="text-sm text-muted-foreground">
                  Enviar informe gerencial todos los días
                </p>
              </div>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig({...config, enabled: checked})}
              disabled={saving}
            />
          </div>
          
          {/* Schedule Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Hora de envío</label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  max="23"
                  value={config.schedule_hour}
                  onChange={(e) => setConfig({...config, schedule_hour: parseInt(e.target.value) || 0})}
                  className="w-20"
                  disabled={saving}
                />
                <span className="self-center">:</span>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={config.schedule_minute}
                  onChange={(e) => setConfig({...config, schedule_minute: parseInt(e.target.value) || 0})}
                  className="w-20"
                  disabled={saving}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Hora del servidor (UTC)</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Email(s) destinatario</label>
              <EmailTagInput
                emails={config.notification_emails}
                onChange={(emails) => setConfig({ ...config, notification_emails: emails })}
                placeholder="Agregar email de destinatario..."
              />
            </div>
          </div>
          
          {/* Status */}
          {config.last_sent && (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded">
              <CheckCircle className="w-4 h-4" />
              Último envío: {new Date(config.last_sent).toLocaleString('es-CL')}
            </div>
          )}
          
          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button 
              variant="outline"
              onClick={sendNow}
              disabled={sending || config.notification_emails.length === 0}
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Enviar Ahora</>
              )}
            </Button>
            
            <Button onClick={saveConfig} disabled={saving}>
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
              ) : (
                'Guardar Configuración'
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Report Content Info */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-green-600" />
            Contenido del Informe
          </h2>
          <Button variant="outline" size="sm" onClick={loadPreview} disabled={loadingPreview}>
            {loadingPreview ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Ver Vista Previa</>
            )}
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-3 bg-blue-50 rounded border border-blue-200">
            <div className="flex items-center gap-2 text-blue-700 mb-2">
              <BarChart3 className="w-5 h-5" />
              <span className="font-medium">1. OTs en Ejecución</span>
            </div>
            <p className="text-sm text-blue-600">
              Control de gastos: HH y materiales vs presupuesto, alertas de exceso
            </p>
          </div>
          
          <div className="p-3 bg-green-50 rounded border border-green-200">
            <div className="flex items-center gap-2 text-green-700 mb-2">
              <DollarSign className="w-5 h-5" />
              <span className="font-medium">2. Facturación del Mes</span>
            </div>
            <p className="text-sm text-green-600">
              Total facturado, cobrado y pendiente del mes actual
            </p>
          </div>
          
          <div className="p-3 bg-red-50 rounded border border-red-200">
            <div className="flex items-center gap-2 text-red-700 mb-2">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">3. Cuentas por Pagar</span>
            </div>
            <p className="text-sm text-red-600">
              Facturas vencidas y calendario de vencimientos próximos
            </p>
          </div>
          
          <div className="p-3 bg-orange-50 rounded border border-orange-200">
            <div className="flex items-center gap-2 text-orange-700 mb-2">
              <Calendar className="w-5 h-5" />
              <span className="font-medium">4. Activo Circulante</span>
            </div>
            <p className="text-sm text-orange-600">
              OTs abiertas con montos por facturar y % de avance
            </p>
          </div>
        </div>
      </div>

      {/* Preview Data */}
      {previewData && (
        <div className="bg-white rounded-lg border p-4">
          <h2 className="font-semibold text-lg mb-4">Vista Previa de Datos</h2>
          
          {/* OTs Summary */}
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <h3 className="font-medium mb-2">OTs en Ejecución ({previewData.ots_en_ejecucion?.cantidad_ots || 0})</h3>
            <div className="grid grid-cols-4 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Presupuesto:</span>
                <span className="ml-1 font-medium">{fmt(previewData.ots_en_ejecucion?.totales?.presupuesto)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Costo HH:</span>
                <span className="ml-1 font-medium">{fmt(previewData.ots_en_ejecucion?.totales?.costo_hh)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Materiales:</span>
                <span className="ml-1 font-medium">{fmt(previewData.ots_en_ejecucion?.totales?.costo_materiales)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Diferencia:</span>
                <span className={`ml-1 font-medium ${previewData.ots_en_ejecucion?.totales?.diferencia < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {fmt(previewData.ots_en_ejecucion?.totales?.diferencia)}
                </span>
              </div>
            </div>
          </div>
          
          {/* Facturación */}
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <h3 className="font-medium mb-2">Facturación {previewData.facturacion_mes?.mes}</h3>
            <div className="grid grid-cols-4 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Facturas:</span>
                <span className="ml-1 font-medium">{previewData.facturacion_mes?.cantidad_facturas || 0}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Total Neto:</span>
                <span className="ml-1 font-medium">{fmt(previewData.facturacion_mes?.total_neto)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Cobrado:</span>
                <span className="ml-1 font-medium text-green-600">{fmt(previewData.facturacion_mes?.cobrado)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Pendiente:</span>
                <span className="ml-1 font-medium text-orange-600">{fmt(previewData.facturacion_mes?.pendiente)}</span>
              </div>
            </div>
          </div>
          
          {/* Cuentas por Pagar */}
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <h3 className="font-medium mb-2">Cuentas por Pagar</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-red-600 font-medium mb-1">Vencidas: {fmt(previewData.cuentas_por_pagar?.vencidas?.total)}</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span>Hasta 30d: {fmt(previewData.cuentas_por_pagar?.vencidas?.hasta_30_dias)}</span>
                  <span>31-60d: {fmt(previewData.cuentas_por_pagar?.vencidas?.['31_a_60_dias'])}</span>
                  <span>61-90d: {fmt(previewData.cuentas_por_pagar?.vencidas?.['61_a_90_dias'])}</span>
                  <span>+90d: {fmt(previewData.cuentas_por_pagar?.vencidas?.mas_90_dias)}</span>
                </div>
              </div>
              <div>
                <p className="text-orange-600 font-medium mb-1">Por Vencer: {fmt(previewData.cuentas_por_pagar?.por_vencer?.total)}</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span>7 días: {fmt(previewData.cuentas_por_pagar?.por_vencer?.proximos_7_dias)}</span>
                  <span>8-30d: {fmt(previewData.cuentas_por_pagar?.por_vencer?.['8_a_30_dias'])}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Activo Circulante */}
          <div className="p-3 bg-gray-50 rounded">
            <h3 className="font-medium mb-2">Activo Circulante ({previewData.activo_circulante?.cantidad_ots || 0} OTs)</h3>
            <div className="grid grid-cols-4 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Presupuesto:</span>
                <span className="ml-1 font-medium">{fmt(previewData.activo_circulante?.totales?.presupuesto)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Facturado:</span>
                <span className="ml-1 font-medium text-green-600">{fmt(previewData.activo_circulante?.totales?.facturado)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Por Facturar:</span>
                <span className="ml-1 font-medium text-orange-600">{fmt(previewData.activo_circulante?.totales?.por_facturar)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">% Facturado:</span>
                <span className="ml-1 font-medium">{(previewData.activo_circulante?.totales?.porcentaje_facturado || 0).toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">Información</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>El informe se envía automáticamente todos los días a la hora configurada</li>
          <li>Incluye los 4 indicadores clave solicitados para gerencia</li>
          <li>Los datos se calculan en tiempo real al momento del envío</li>
          <li>Use "Enviar Ahora" para recibir una copia inmediata del informe</li>
          <li>El costo de HH se calcula a $15.000/hora (configurable)</li>
        </ul>
      </div>
    </div>
  );
};

export default GerenciaReport;
